import { Box, Grid } from '@domain-group/suit-theme'

import NoticeBox from '@components/NoticeBox'

import useCarousel from '@hooks/useCarousel'
import useFlipperKey from '@hooks/useFlipperKey'
import useIdSort from '@hooks/useIdSort'

import type { ImageModel } from '@models/types'

import ImageTile from './ImageTile'
import Preview from './Preview'

import type { UseCampaignImagesPayload } from '../../../hooks/useCampaignImages'

interface GridViewProps {
  campaignImagesPayload: UseCampaignImagesPayload,
  largeGrid?: boolean,
}

const GridView = (props: GridViewProps) => {
  const { campaignImagesPayload, largeGrid = false } = props

  const {
    bulkSelecting,
    callbacks: { toggleId, updateImageSort },
    campaign,
    exportSizeErrorImageIds,
    filteredImages,
    hasPublicImages,
    selectedEntityIds,
  } = campaignImagesPayload

  const { isEnabled: photosPlansV2 } = useFlipperKey('photos_plans_v2')

  const idSortPayload = useIdSort({
    callbacks: { updateSort: updateImageSort },
    idsArray: filteredImages.map(image => image.id),
  })
  const { callbacks: { modifySort }, sortedIds } = idSortPayload

  const imagesById = filteredImages.reduce((map, image) => {
    map[image.id] = image
    return map
  }, {} as Record<number, ImageModel>)

  const sortedImages = sortedIds.map(imageId => imagesById[imageId]).filter(Boolean)

  const carouselPayload = useCarousel({ items: sortedImages })
  const { callbacks: { setSelectedIndex }, hasSelectedIndex, selectedIndex } = carouselPayload

  const anySelected = selectedEntityIds.length > 0
  const canReorder = !!photosPlansV2 && !hasSelectedIndex

  const dropImage = (fromImageId: number, toImageId: number) => {
    if (fromImageId === toImageId) return

    const fromIndex = sortedIds.indexOf(fromImageId)
    const toIndex = sortedIds.indexOf(toImageId)

    if (fromIndex === -1 || toIndex === -1) return

    modifySort(fromIndex, toIndex)
  }

  const renderTile = (image: ImageModel, index: number) => {
    const selectionIndex = selectedEntityIds.findIndex(imageId => image.id === imageId)
    const hasExportSizeError = exportSizeErrorImageIds.includes(image.id)

    return (
      <ImageTile
        anySelected={anySelected}
        bulkSelecting={bulkSelecting}
        callbacks={{
          toggleId,
          dropImage: (fromImageId: number) => dropImage(fromImageId, image.id),
          selectImage: () => setSelectedIndex(index),
        }}
        campaign={campaign}
        canReorder={canReorder}
        fadePrivate={hasPublicImages}
        hasExportSizeError={hasExportSizeError}
        hoverActionsEnabled={!!photosPlansV2}
        image={image}
        key={image.id}
        selectionIndex={selectionIndex}
      />
    )
  }

  return (
    <Box flexDirection="column" flexShrink={0}>
      {hasSelectedIndex && (
        <Preview
          callbacks={{ unselectImage: () => setSelectedIndex(undefined) }}
          campaign={campaign}
          carouselPayload={carouselPayload}
          image={sortedImages[selectedIndex!]}
        />
      )}

      {canReorder && (
        <Box paddingTop="large" paddingX="large">
          <NoticeBox variant="whiteGrey">
            <NoticeBox.Title>Manage Images</NoticeBox.Title>

            <NoticeBox.Paragraph boxProps={{ marginTop: 0 }}>
              Drag and drop images to reorder them.
            </NoticeBox.Paragraph>
          </NoticeBox>
        </Box>
      )}

      <Grid
        // @ts-expect-error TS2322
        gridGap="medium"
        gridAutoRows="auto"
        gridTemplateColumns={largeGrid ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(250px, 1fr))'}
        padding="large"
      >
        {sortedImages.map(renderTile)}
      </Grid>
    </Box>
  )
}

export default GridView
